// @ts-nocheck
// Ported from v1 components/partials/dashboard/HeaderStyle/header.js:770-880
// (loginAs / logoutAs). Extracted here as standalone helpers so the header
// UI can call them without pulling the whole legacy header code, and so
// tests can drive them directly.
//
// v1 encoded the whole flow inline in the header component: hit the
// login-as/logout-from API, flip `active_profile: true|false` on the
// matching profile in Storage.user, persist activeLoginType to session +
// local storage, then either mount or unmount the softphone by requiring
// demo-1 and clicking a hidden `keypadConnect2` / `disconnect2` button.
//
// The button-click trick works because demo-1 (now
// @/features/cti/softphone/demo-1) attaches its .forEach() click handlers
// to whatever elements match those class names at initDailer() time.
// initDailer() runs once when the module imports, so calling
// `import('./demo-1')` here is idempotent — subsequent calls hit the
// module cache and the handlers stay bound.

import { postApiCall } from '@/service/httpCall';
import { API_USER_LOGIN_AS, API_USER_LOGOUT_FROM } from '@/service/url';
import { Storage, persistActiveLoginType, storeUserSync } from '@/utilities/setting';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { trackActivity, isAppLogoutInProgress } from '@/service/activityTracker';

const clickAll = (className: string) => {
  const els = document.getElementsByClassName(className);
  // Copy first — a live HTMLCollection can shift while its elements react
  // to click(). Byte-identical to v1's `for (let o of buttonObj) o.click()`.
  Array.from(els).forEach((el: any) => el.click?.());
};

let profileSwitchInFlight: Promise<any> | null = null;

const runExclusive = <T,>(fn: () => Promise<T>): Promise<T> => {
  if (profileSwitchInFlight) return profileSwitchInFlight as Promise<T>;
  const p = fn().finally(() => {
    if (profileSwitchInFlight === p) profileSwitchInFlight = null;
  });
  profileSwitchInFlight = p;
  return p;
};

const dispatchActiveLoginTypeChange = (type: string | null) => {
  try {
    window.dispatchEvent(
      new CustomEvent('sessionStorageChange', {
        detail: { key: 'activeLoginType', value: type },
      })
    );
  } catch { /* noop */ }
};

/**
 * Flip an agent's telephony profile ON. Byte-identical to v1
 * loginAs(type) in header.js:770-829.
 *
 * When `type === 'WEBRTC'`, this also lazily imports the softphone
 * module (demo-1) and clicks all `keypadConnect2` elements to trigger
 * SIP.connect() + register().
 */
export function loginAsProfile(userId: string | number, type: string) {
  return runExclusive(async () => {
  try {
    const resp: any = await postApiCall(`${API_USER_LOGIN_AS}/${userId}`, { type });

    // Update Storage.user so subsequent reads see the new active_profile.
    const rawUser = Storage.getItem('user');
    const user: any = rawUser ? JSON.parse(rawUser) : null;
    if (user && user.agentData?.agentTelephony?.profile) {
      user.agentData.agentTelephony.profile = user.agentData.agentTelephony.profile.map((p: any) => ({
        ...p,
        active_profile: p.type === type,
      }));
      storeUserSync(user);
    }

    if (type === 'WEBRTC') {
      // Lazy-import the softphone (same as CTIAgentUI's effect). Once its
      // initDailer() runs, DOM buttons carry the SIP handlers.
      await import('./demo-1');
      clickAll('keypadConnect2');
    }

    persistActiveLoginType(type);
    dispatchActiveLoginTypeChange(type);
    trackActivity('login', { profile_type: type });
    toast.success(`Logged In as: ${type}`);
    return resp;
  } catch (error: any) {
    toast.error(friendlyApiError(error) || `Error while Logged In as: ${type}`);
    console.error('loginAsProfile error:', error);
    throw error;
  }
  });
}

/**
 * Flip an agent's telephony profile OFF. Byte-identical to v1
 * logoutAs(type) in header.js:831-880.
 *
 * When `type === 'WEBRTC'`, this also loads the softphone module (so its
 * disconnect2 handler is bound) and clicks all `disconnect2` elements to
 * SIP.unregister() + disconnect(), then clears session-scoped state.
 */
export function logoutAsProfile(userId: string | number, type: string) {
  return runExclusive(async () => {
  try {
    const resp: any = await postApiCall(`${API_USER_LOGOUT_FROM}/${userId}`, { type });

    const rawUser = Storage.getItem('user');
    const user: any = rawUser ? JSON.parse(rawUser) : null;
    if (user && user.agentData?.agentTelephony?.profile) {
      user.agentData.agentTelephony.profile = user.agentData.agentTelephony.profile.map((p: any) => ({
        ...p,
        active_profile: p.type === type ? false : p.active_profile,
      }));
      storeUserSync(user);
    }

    if (type === 'WEBRTC') {
      await import('./demo-1');
      sessionStorage.setItem('showWebrtc', 'NO');
      try {
        const modals = JSON.parse(sessionStorage.getItem('activeModal') || '[]');
        const updated = Array.isArray(modals) ? modals.filter((m) => m !== 'WEB') : [];
        sessionStorage.setItem('activeModal', JSON.stringify(updated));
      } catch { /* noop */ }
      clickAll('disconnect2');
      sessionStorage.removeItem('webrtcPhoneStatus');
    }

    // v1 picks the next-active type from the API response (`activeProfile.type`).
    const nextActive: string | null =
      (Array.isArray(resp) ? resp.find((item: any) => item?.type === true)?.type : null) || null;
    persistActiveLoginType(nextActive);
    dispatchActiveLoginTypeChange(nextActive);

    if (!isAppLogoutInProgress()) {
      trackActivity('logout', { profile_type: type, scope: 'profile' });
    }
    toast.success(`Logged Out From: ${type}`);
    return { resp, nextActive };
  } catch (error: any) {
    toast.error(friendlyApiError(error) || `Error while Logging Out from: ${type}`);
    console.error('logoutAsProfile error:', error);
    throw error;
  }
  });
}
